import { AuthenticationManager } from "@playit/shared-authentication";
import axios from "axios";
import { CharacterPartDTO, CharacterPartType } from "../dto/CharacterPartDTO";
import { CharacterPartDetailsDTO } from "../dto/CharacterPartDetailsDTO";
import { CharacterPartGenderedPairDTO } from "../dto/CharacterPartGenderedPairDTO";
import { CharacterPartGenderedPairDetailsDTO } from "../dto/CharacterPartGenderedPairDetailsDTO";
import { CharacterPartGenderedPairRenderDTO } from "../dto/CharacterPartGenderedPairRenderDTO";

export class CharacterPartService {
  private static readonly baseUrl = `${process.env.REACT_APP_PLAYIT_CREATORAPI}`;

  private static getAuthConfig() {
    const token = AuthenticationManager.GetToken();

    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  }

  private static getPagedConfig(
    page: number,
    pageSize: number,
    name?: string,
    type?: CharacterPartType
  ) {
    return {
      ...this.getAuthConfig(),
      params: {
        PageSize: pageSize,
        PageNumber: page,
        Name: name,
        Type: type,
      },
    };
  }

  private static getTotalCount(
    headers: Record<string, unknown> | undefined
  ) {
    const paginationHeader = headers?.["x-pagination"];
    if (typeof paginationHeader !== "string") {
      throw new Error("Missing x-pagination header");
    }

    return JSON.parse(paginationHeader)["totalCount"];
  }

  static async GetCharacterParts(
    orgId: string,
    page: number,
    pageSize: number,
    name?: string,
    type?: CharacterPartType
  ) {
    const path = `${this.baseUrl}/organizations/${orgId}/character-parts`;
    const config = this.getPagedConfig(page, pageSize, name, type);

    try {
      const { data, headers } = await axios.get<CharacterPartDTO[]>(path, config);
      const totalCount = this.getTotalCount(headers);

      return {
        charParts: data ?? [],
        totalCount: totalCount,
      };
    } catch (error) {
      console.error(error);
      return undefined;
    }
  }

  static async GetCharacterPartDetails(orgId: string, characterPartId: string) {
    const path = `${this.baseUrl}/organizations/${orgId}/character-parts/${characterPartId}`;
    const config = this.getAuthConfig();

    try {
      const { data, status } = await axios.get<CharacterPartDetailsDTO>(
        path,
        config
      );
      return status === 200 ? data : undefined;
    } catch (error) {
      console.error(error);
      return undefined;
    }
  }

  static async GetCharacterPartGenderedPairs(
    orgId: string,
    page: number,
    pageSize: number,
    name?: string,
    type?: CharacterPartType
  ) {
    const path = `${this.baseUrl}/organizations/${orgId}/character-part-gendered-pairs`;
    const config = this.getPagedConfig(page, pageSize, name, type);

    try {
      const { data, headers } = await axios.get<CharacterPartGenderedPairDTO[]>(
        path,
        config
      );
      const totalCount = this.getTotalCount(headers);

      return {
        charPartPairs: data ?? [],
        totalCount,
      };
    } catch (error) {
      console.error(error);
      return undefined;
    }
  }

  static async GetCharacterPartGenderedPairDetails(
    orgId: string,
    characterPartPairName: string
  ) {
    const path = `${this.baseUrl}/organizations/${orgId}/character-part-gendered-pairs/${characterPartPairName}`;
    const config = this.getAuthConfig();

    try {
      const { data, status } =
        await axios.get<CharacterPartGenderedPairDetailsDTO>(path, config);
      return status === 200 ? data : undefined;
    } catch (error) {
      console.error(error);
      return undefined;
    }
  }

  static async GetCharacterPartGenderedPairRender(
    orgId: string,
    characterPartPairName: string
  ) {
    const path = `${this.baseUrl}/organizations/${orgId}/character-part-gendered-pairs/${characterPartPairName}/render`;
    const config = this.getAuthConfig();

    try {
      const { data, status } =
        await axios.get<CharacterPartGenderedPairRenderDTO>(path, config);
      return status === 200 ? data : undefined;
    } catch (error) {
      console.error(error);
      return undefined;
    }
  }
}
